// Shared types used by both portals — extracted from apps/*/src/util/util.tsx
// These are the reconciled types from both codebases.

export const ACCOUNT_TYPES = [
  "RETIREMENT_TRADITIONAL",
  "RETIREMENT_ROTH",
  "RETIREMENT_ROLLOVER",
  "RETIREMENT_TAXABLE",
  "JUST_INVEST",
  "MAJOR_PURCHASE",
  "SUPPLEMENTAL_INCOME",
  "HIGH_YIELD_CASH",
] as const;

export const GOALS = [
  "Just Invest",
  "Retirement",
  "Major Purchase",
  "Supplement Income",
  "High Yield Cash",
] as const;

export const ASSET_LIABILITY = {
  ASSET: "asset",
  LIABILITY: "liability",
} as const;

export type AccountType = (typeof ACCOUNT_TYPES)[number];
export type GoalType = (typeof GOALS)[number];
export type AssetLiability =
  (typeof ASSET_LIABILITY)[keyof typeof ASSET_LIABILITY];
export type AllocationType =
  | "Growth"
  | "Capital Preservation"
  | "Money Market"
  | "Yield";

export type DepositInfo = {
  account_id: string;
  plaid_id: string;
  deposit_id: string;
  created_at: string;
  amount: string;
  state: string;
  message: string;
  state_category: "SUCCESS" | "FAILED" | "PENDING" | "CANCELED" | "UNKNOWN";
};

export type WithdrawalInfo = {
  account_id: string;
  plaid_id: string;
  withdrawal_id: string;
  created_at: string;
  amount: string;
  state: string;
  message: string;
  state_category: "SUCCESS" | "FAILED" | "PENDING" | "CANCELED" | "UNKNOWN";
};

export type ApexAccount = {
  id: string;
  name: string;
  type: AccountType;
  total_equity: number;
  asset_or_liability: AssetLiability;
  total_equity_date?: Date | null;
  holdings?: unknown[];
  allocation?: {
    kind: string;
    risk_score: number;
    time_horizon_years: number;
  } | null;
  transferList?: {
    deposits?: unknown[];
    withdrawals?: unknown[];
  };
  cash_available_for_withdraw?: string;
};

export type AccountInfo = {
  goal: AccountType;
  account_detail: unknown;
  cash_available_for_withdraw: string;
  open_balance: string;
  total_equity: string;
  buying_power: string;
  deposits?: DepositInfo[];
  withdrawals?: WithdrawalInfo[];
  holdings?: unknown[];
  portfolios?: Partial<Record<AllocationType, number>>;
  historical_positions?: unknown[];
};

export type HoldingPositionNumerical = {
  amount: number;
  unit: "USD" | "Unit";
  [key: string]: unknown;
};

export function accountTypeToGoalType(type: AccountType): GoalType {
  switch (type) {
    case "RETIREMENT_TRADITIONAL":
    case "RETIREMENT_ROTH":
    case "RETIREMENT_TAXABLE":
    case "RETIREMENT_ROLLOVER":
      return "Retirement";
    case "JUST_INVEST":
      return "Just Invest";
    case "MAJOR_PURCHASE":
      return "Major Purchase";
    case "SUPPLEMENTAL_INCOME":
      return "Supplement Income";
    case "HIGH_YIELD_CASH":
      return "High Yield Cash";
  }
}

export function accountTypeToName(type: AccountType): string {
  switch (type) {
    case "RETIREMENT_TRADITIONAL":
      return "Traditional IRA";
    case "RETIREMENT_ROTH":
      return "Roth IRA";
    case "RETIREMENT_TAXABLE":
      return "Taxable Retirement";
    case "RETIREMENT_ROLLOVER":
      return "Rollover IRA";
    case "JUST_INVEST":
      return "Just Invest";
    case "MAJOR_PURCHASE":
      return "Major Purchase";
    case "SUPPLEMENTAL_INCOME":
      return "Supplement Income";
    case "HIGH_YIELD_CASH":
      return "High Yield Cash";
  }
}

export function sum<T>(arr: T[], selector: (item: T) => number): number {
  return arr.reduce((total, item) => total + selector(item), 0);
}
